import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";

import api from "api";

import { APIError } from "./api";

// (forType, entityType) pairs an anonymous visitor may request.
// The API still requires the parent resource to be public.
// Keep in sync with ANONYMOUS_DOWNLOAD_ALLOWLIST in simplydo/storage.py.
const ANONYMOUS_DOWNLOAD_ALLOWLIST: Record<string, readonly string[]> = {
  challenge: ["attachment"],
  organisation: ["customPage"],
  poll: ["introText", "outroText", "question"],
};

export const isAnonymousDownloadAllowed = (
  forType?: string,
  entityType?: string,
) => {
  if (!forType || !entityType) {
    return false;
  }
  return ANONYMOUS_DOWNLOAD_ALLOWLIST[forType]?.includes(entityType) ?? false;
};

export const openDownloadLink = (url: string, downloadName: string) => {
  const isLocalhost = url.startsWith(
    import.meta.env.VITE_WEB_URL ?? "http://localhost:4000",
  );
  const isSimplydo = /https:\/\/files(-alpha|-staging)?\.simplydo\.co\.uk/.test(
    url,
  );

  // In almost all regular cases we will operate on simplydo domains (e.g. *.simplydo.co.uk),
  // however in the case of truly cross-origin custom domains the request becomes cross-origin.
  // This can cause issues in browsers when clicking a `download` attribute link, in those cases we open the file in a new tab
  if (isLocalhost || isSimplydo) {
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    // a.target = "_self";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

export const fileDownload = (
  user:
    | (OpenAPI.Schemas["User"] & {
        ownerOrganisation?: OpenAPI.Schemas["Organisation"];
      })
    | UserMe
    | null
    | undefined,
  downloadUrl: string,
  downloadName: string,
  onError: (error: APIError) => void,
  openLink = openDownloadLink,
) => {
  if (!user || !downloadUrl) {
    return;
  }

  const urlData = new URL(downloadUrl);
  const parameters = urlData.searchParams;
  const forType = parameters.get("forType");
  const forId = parameters.get("forId");
  const entityType = parameters.get("entityType");
  const entityId = parameters.get("entityId");
  const identifier = parameters.get("identifier");

  const cookieParams: OpenAPI.GET<"/uploads/download/cookies">["parameters"]["query"] =
    {};

  if (forType) {
    cookieParams.forType = forType;
  }
  if (forId) {
    cookieParams.forId = forId;
  }
  if (entityType) {
    cookieParams.entityType = entityType;
  }
  if (entityId) {
    cookieParams.entityId = entityId;
  }
  if (identifier) {
    cookieParams.identifier = identifier;
  }

  api.uploads.getFileDownloadCookies(
    cookieParams,
    ({ url }) => {
      openLink(url, downloadName);
    },
    (error) => {
      onError(error);
    },
  );
};
